<template>
    <div class="row">
        <div class="col-12">
            <card>
                <div class="card-body">
                    <h2 class="pt-0 mt-0">
                        <i class="fas fa-file-import text-primary"></i> {{$t('part.titleimport')}}
                    </h2>
                    <hr />

                    <div v-if="stage === 'select'">
                        <div class="row">
                            <div class="col-md-6">
                                <label>{{$t('part.text.chooseimportfile')}}</label>
                                <b-form-file
                                    accept=".xlsx,.xls,.csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
                                    v-model="file"
                                    :placeholder="$t('text.chooseafile')"
                                ></b-form-file>
                                <small class="text-muted">{{$t('part.text.allowedfiletypesimport')}}</small>
                            </div>
                        </div>

                        <div v-if="formatErrors.length" class="alert alert-danger mt-4">
                            <strong>{{$t('part.text.invalidfile')}}</strong>
                            <ul class="mb-0">
                                <li v-for="(error, index) in formatErrors" :key="index">{{error}}</li>
                            </ul>
                        </div>

                        <div class="mt-4">
                            <p-button type="primary" :disabled="!file || loading" @click.native.prevent="evaluate">
                                <i class="fas fa-search fa-fw"></i> {{$t('part.button.evaluatefile')}}
                            </p-button>
                            <span v-if="loading" class="ml-2">
                                <i class="fas fa-spinner fa-spin"></i> {{$t('part.text.processing')}}
                            </span>
                        </div>
                    </div>

                    <div v-else>
                        <div class="alert" :class="stage === 'done' ? 'alert-success' : 'alert-info'">
                            <span v-if="stage === 'preview'">{{$t('part.text.importpreviewready')}}</span>
                            <span v-else>{{$t('part.message.successimport')}}</span>
                        </div>

                        <div class="row text-center mb-4">
                            <div class="col-3">
                                <h4>{{summary.total}}</h4>
                                <small class="text-muted">{{$t('part.text.total')}}</small>
                            </div>
                            <div class="col-3">
                                <h4 class="text-success">{{ stage === 'done' ? summary.created : summary.to_create }}</h4>
                                <small class="text-muted">{{$t('part.text.tocreate')}}</small>
                            </div>
                            <div class="col-3">
                                <h4 class="text-primary">{{ stage === 'done' ? summary.updated : summary.to_update }}</h4>
                                <small class="text-muted">{{$t('part.text.toupdate')}}</small>
                            </div>
                            <div class="col-3">
                                <h4 class="text-danger">{{summary.errors}}</h4>
                                <small class="text-muted">{{$t('part.text.errors')}}</small>
                            </div>
                        </div>

                        <b-tabs content-class="mt-3">
                            <b-tab :title="$t('part.text.tabsall') + ' (' + displayRows.length + ')'" active>
                                <v-client-table :data="displayRows" :columns="columns" :options="tableOptions">
                                    <template slot="acoes" slot-scope="props">
                                        <router-link
                                            v-if="props.row.id"
                                            class="btn btn-xs btn-primary"
                                            :to="{ name: 'parts_edit', params: { id: props.row.id } }"
                                        >
                                            <i class="fas fa-edit fa-fw"></i> {{$t('part.button.editpart')}}
                                        </router-link>
                                    </template>
                                </v-client-table>
                            </b-tab>
                            <b-tab :title="createdTabTitle + ' (' + createdRows.length + ')'">
                                <v-client-table :data="createdRows" :columns="columns" :options="tableOptions">
                                    <template slot="acoes" slot-scope="props">
                                        <router-link
                                            v-if="props.row.id"
                                            class="btn btn-xs btn-primary"
                                            :to="{ name: 'parts_edit', params: { id: props.row.id } }"
                                        >
                                            <i class="fas fa-edit fa-fw"></i> {{$t('part.button.editpart')}}
                                        </router-link>
                                    </template>
                                </v-client-table>
                            </b-tab>
                            <b-tab :title="updatedTabTitle + ' (' + updatedRows.length + ')'">
                                <v-client-table :data="updatedRows" :columns="columns" :options="tableOptions">
                                    <template slot="acoes" slot-scope="props">
                                        <router-link
                                            v-if="props.row.id"
                                            class="btn btn-xs btn-primary"
                                            :to="{ name: 'parts_edit', params: { id: props.row.id } }"
                                        >
                                            <i class="fas fa-edit fa-fw"></i> {{$t('part.button.editpart')}}
                                        </router-link>
                                    </template>
                                </v-client-table>
                            </b-tab>
                            <b-tab :title="$t('part.text.tabserrors') + ' (' + errorRows.length + ')'">
                                <v-client-table :data="errorRows" :columns="columns" :options="tableOptions">
                                    <template slot="acoes" slot-scope="props">
                                        <router-link
                                            v-if="props.row.id"
                                            class="btn btn-xs btn-primary"
                                            :to="{ name: 'parts_edit', params: { id: props.row.id } }"
                                        >
                                            <i class="fas fa-edit fa-fw"></i> {{$t('part.button.editpart')}}
                                        </router-link>
                                    </template>
                                </v-client-table>
                            </b-tab>
                        </b-tabs>

                        <div class="mt-4">
                            <p-button
                                v-if="stage === 'preview'"
                                type="primary"
                                :disabled="loading"
                                @click.native.prevent="confirmImport"
                            >
                                <i class="fas fa-check fa-fw"></i> {{$t('part.button.confirmimport')}}
                            </p-button>
                            <p-button type="default" :disabled="loading" @click.native.prevent="reset">
                                <i class="fas fa-redo fa-fw"></i> {{$t('part.button.newimport')}}
                            </p-button>
                            <span v-if="loading" class="ml-2">
                                <i class="fas fa-spinner fa-spin"></i> {{$t('part.text.processing')}}
                            </span>
                        </div>
                    </div>
                </div>
            </card>
        </div>
    </div>
</template>
<script>
export default {
    data() {
        return {
            file: null,
            loading: false,
            stage: "select",
            result: null
        };
    },
    computed: {
        formatErrors() {
            return this.result && !this.result.valid ? this.result.format_errors : [];
        },
        summary() {
            return this.result ? this.result.summary : { total: 0, to_create: 0, to_update: 0, created: 0, updated: 0, errors: 0 };
        },
        displayRows() {
            if (!this.result || !this.result.rows) {
                return [];
            }
            return this.result.rows.map(row => ({
                row: row.row,
                sku: row.sku,
                name: row.name,
                code: row.code,
                price: row.price,
                situacao: this.situacaoLabel(row),
                motivo: (row.errors || []).join(" "),
                id: row.id || null,
                _action: row.action,
                _status: row.status,
                _hasErrors: !!(row.errors && row.errors.length)
            }));
        },
        createdRows() {
            return this.displayRows.filter(row => row._action === "create" || row._status === "created");
        },
        updatedRows() {
            return this.displayRows.filter(row => row._action === "update" || row._status === "updated");
        },
        errorRows() {
            return this.displayRows.filter(row => row._hasErrors || row._status === "error");
        },
        createdTabTitle() {
            return this.stage === "done" ? this.$t("part.text.tabscreated") : this.$t("part.text.tabstocreate");
        },
        updatedTabTitle() {
            return this.stage === "done" ? this.$t("part.text.tabsupdated") : this.$t("part.text.tabstoupdate");
        },
        columns() {
            return ["row", "sku", "name", "code", "price", "situacao", "motivo", "acoes"];
        },
        tableOptions() {
            return {
                perPage: 25,
                sortable: ["row", "sku", "name", "code", "price", "situacao"],
                headings: {
                    row: this.$t("part.text.row"),
                    sku: "SKU",
                    name: this.$t("part.name"),
                    code: this.$t("part.code"),
                    price: this.$t("part.price"),
                    situacao: this.$t("part.text.situation"),
                    motivo: this.$t("part.text.reason"),
                    acoes: this.$t("part.text.actions")
                },
                texts: {
                    count: this.$t("field.count"),
                    filter: this.$t("field.filter"),
                    filterPlaceholder: this.$t("field.filterplaceholder"),
                    limit: this.$t("field.limit")
                },
                sortIcon: { base: "fas", up: "fa-sort-up", down: "fa-sort-down", is: "fa-sort" }
            };
        }
    },
    methods: {
        situacaoLabel(row) {
            if (row.status === "created") return this.$t("part.text.situationcreated");
            if (row.status === "updated") return this.$t("part.text.situationupdated");
            if (row.status === "error" || (row.errors && row.errors.length)) return this.$t("part.text.situationerror");
            if (row.action === "create") return this.$t("part.text.situationtocreate");
            if (row.action === "update") return this.$t("part.text.situationtoupdate");
            return "";
        },
        evaluate() {
            this.loading = true;
            this.$store
                .dispatch("parts/importPreview", this.file)
                .then(resp => {
                    this.loading = false;
                    this.result = resp.data;
                    if (this.result.valid) {
                        this.stage = "preview";
                    }
                })
                .catch(() => {
                    this.loading = false;
                    this.$notify({
                        type: "danger",
                        horizontalAlign: "center",
                        message: this.$t("part.message.errorimport")
                    });
                });
        },
        confirmImport() {
            this.loading = true;
            this.$store
                .dispatch("parts/importConfirm", this.result.token)
                .then(resp => {
                    this.loading = false;
                    this.result = resp.data;
                    this.stage = "done";
                    this.$notify({
                        type: "success",
                        horizontalAlign: "center",
                        message: this.$t("part.message.successimport")
                    });
                })
                .catch(() => {
                    this.loading = false;
                    this.$notify({
                        type: "danger",
                        horizontalAlign: "center",
                        message: this.$t("part.message.errorimport")
                    });
                });
        },
        reset() {
            this.file = null;
            this.result = null;
            this.stage = "select";
        }
    }
};
</script>
<style></style>
